import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';
import ReactMarkdown from 'react-markdown';
import { localizeChineseText } from '../services/chineseText';

const Chatbot = ({ baziContext, t, lang }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const logRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    setMessages(
      baziContext?.ai_interpretation
        ? [{ role: 'assistant', content: localizeChineseText(baziContext.ai_interpretation, lang) }]
        : []
    );
  }, [baziContext, lang]);

  useEffect(() => {
    // Scroll the log itself rather than the page, so the reading stays put
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const userMessage = (text ?? input).trim();
    if (!userMessage || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    try {
      const chatHistory = messages.map((msg) => ({ role: msg.role, content: msg.content }));
      const response = await api.post('/chat', {
        message: userMessage,
        bazi_context: baziContext,
        chat_history: chatHistory,
        lang
      });
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: localizeChineseText(response.data.response, lang) }
      ]);
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || '';
      console.error('Chat error:', detail, error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: localizeChineseText(detail ? `${t('chatError')}\n\n\`${detail}\`` : t('chatError'), lang)
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const quickQuestions = [t('q1'), t('q2'), t('q3'), t('q4'), t('q5')];

  return (
    <section className="bz-ask">
      <div className="fc-chat">
        <div className="fc-chat__head">
          <h2 className="fc-chat__title">{t('chatTitle')}</h2>
        </div>

        <div className="fc-chat__log" ref={logRef}>
          {messages.map((msg, idx) => (
            <div key={idx} className={`fc-msg ${msg.role === 'user' ? 'fc-msg--user' : ''}`}>
              <span className="fc-msg__avatar" aria-hidden="true">
                {msg.role === 'assistant' ? 'FC' : '·'}
              </span>
              <div className="fc-msg__body">
                {msg.role === 'assistant'
                  ? <ReactMarkdown>{msg.content}</ReactMarkdown>
                  : msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="fc-msg">
              <span className="fc-msg__avatar" aria-hidden="true">FC</span>
              <div className="fc-msg__body">
                <span className="fc-typing"><span /><span /><span /></span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="fc-chat__suggest">
          {quickQuestions.map((q, idx) => (
            <button
              key={idx}
              type="button"
              className="fc-btn fc-btn--secondary fc-btn--sm"
              onClick={() => send(q)}
              disabled={loading}
            >
              {q}
            </button>
          ))}
        </div>

        <div className="fc-chat__foot">
          <textarea
            className="fc-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatPlaceholder')}
            rows="1"
            disabled={loading}
          />
          <button
            type="button"
            className="fc-btn fc-btn--primary"
            onClick={() => send()}
            disabled={loading || !input.trim()}
          >
            {t('chatSend')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Chatbot;
